import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants, ImportTemplates } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './TiersComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable, { DATA_MANAGEMENT_PAGE_SIZE } from '../DataManagement/DataManagementDataTable';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

const renderTierActions = (tier: any, onEdit: (tier: any) => void, onDelete: (tier: any) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} aria-label={`Edit ${tier.tierName}`} onClick={() => onEdit(tier)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Delete ${tier.tierName}`} onClick={() => onDelete(tier)} />
  </>
);

interface TiersComponentProps {
  providerTypes: { label: string; value: string }[];
}

const TiersComponent: React.FC<TiersComponentProps> = ({ providerTypes }) => {
  // Inject custom style for dialog overlay to ensure lighter background
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('tiers-component-styles');
    return cleanup;
  }, []);
  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();
    const handleExcelUpload = async (file: File) => {
      setLoading(true);
      try {
        const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.TIERS);
        await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
          fileKey,
          fileName: file.name,
          module: Constants.DATA_MANAGEMENT_MODULES.TIERS,
        });
        setCurrentPage(1);
        await refresh();
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tiers uploaded successfully', life: 3000 });
      } catch (err: any) {
        let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
        toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
      } finally {
        setLoading(false);
      }
    };

    const { showImport, ImportPopup } = useExcelImportPopup({
      templateUrl: ImportTemplates.tiers,
      onUpload: handleExcelUpload,
      title: 'Import Tiers from Excel',
      prominentDownloadLink: true,
    });
  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const query = createDataManagementQuery({ providerType: providerTypeFilter, search: searchTerm });
  const {
    items: tiers,
    totalRecords,
    page: currentPage,
    setPage: setCurrentPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<any>(ApiEndpoints.GetPagedTiers, query, {
    debounceMs: searchTerm ? 300 : 0,
    errorPrefix: 'Failed to fetch tiers',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedTier, setSelectedTier] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<any>(null);

  // Get unique provider types for dropdown (filter)
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes
    : Array.from(new Set(tiers.map(t => t.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));
  let dialogHeader = 'Edit Tier';
  if (isAddMode) dialogHeader = 'Add Tier';
  else if (selectedTier?.tierName) dialogHeader = `Edit Tier - ${selectedTier.tierName}`;

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading tiers" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Tier" icon="pi pi-plus" onClick={() => {
            setSelectedTier({ tierId: 0, tierName: '', additionalTierDetails: '', tierIdentifier: '', isActualTier: false, tierCode: '', sequence: 0, providerType: '', tierLevel: null });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="tier-search-anim">
          <input
            type="text"
            placeholder="Search by Tier Name..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <DataManagementDataTable value={tiers} dataKey="tierId" page={currentPage} totalRecords={totalRecords}
        onPage={setCurrentPage} loading={loading} emptyMessage="No tiers found.">
        <Column header="#" headerStyle={{ width: 64, minWidth: 40 }} body={(_tier: any, options) => (currentPage - 1) * DATA_MANAGEMENT_PAGE_SIZE + options.rowIndex + 1} />
        <Column field="tierName" header="Tier Name" body={(tier: any) => tier.tierName || '-'} />
        <Column field="tierCode" header="Tier Code" body={(tier: any) => tier.tierCode || '-'} />
        <Column field="tierIdentifier" header="Tier Identifier" body={(tier: any) => tier.tierIdentifier || '-'} />
        <Column field="providerType" header="Provider Type" body={(tier: any) => tier.providerType || '-'} />
        <Column field="additionalTierDetails" header="Additional Details" body={(tier: any) => tier.additionalTierDetails || '-'} />
        <Column field="sequence" header="Sequence" body={(tier: any) => tier.sequence ?? '-'} />
        <Column field="tierLevel" header="Tier Level" body={(tier: any) => tier.tierLevel ?? '-'} />
        <Column field="isActualTier" header="Is Actual Tier" body={(tier: any) => tier.isActualTier ? 'Yes' : 'No'} />
        <Column header="Actions" body={(tier: any) => renderTierActions(
          tier,
          currentTier => { setSelectedTier({ ...currentTier }); setIsAddMode(false); setEditDialogVisible(true); },
          currentTier => { setTierToDelete(currentTier); setDeleteDialogVisible(true); },
        )} />
      </DataManagementDataTable>
      <Dialog
        header={dialogHeader}
        visible={editDialogVisible}
        style={{ width: '600px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.tiers, selectedTier);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.tiers, selectedTier);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddTier, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tier added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedTier) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateTier}/${selectedTier.tierId}`, { ...payload, isActive: selectedTier.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Tier updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <TierEditForm editTier={selectedTier} setEditTier={setSelectedTier} providerTypes={providerTypes} isEditing={!isAddMode} />
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!tierToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeleteTier}/${tierToDelete.tierId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setTierToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete tier';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{tierToDelete?.tierName ? `: ${tierToDelete.tierName}` : ''}?
        </div>
      </Dialog>
    </div>
  );
}

interface TierEditFormProps {
  editTier: any;
  setEditTier: (tier: any) => void;
  providerTypes: { label: string; value: string }[];
  isEditing: boolean;
}

const TierEditForm: React.FC<TierEditFormProps> = ({ editTier, setEditTier, providerTypes, isEditing }) => {

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.tiers}
      values={editTier}
      onChange={setEditTier}
      options={{ providerType: providerTypes }}
      isEditing={isEditing}
    />
  );
};


export default TiersComponent;
